import * as THREE from 'three';
import {
  archesLayout,
  createClearingMap,
  denLayout,
  hexLen,
  hexToWorld,
  leapsLayout,
  LEAP_ARCH,
  LEAP_VERT,
  worldToHex,
  type BattleState,
  type CazaPt,
  type MapDef,
} from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, looksRng, Precip, type OmenCtx } from '../omens/common.ts';
import { BOAR_HIP_Y, BOAR_LEGS, boarBody, boarLeg } from '../models/boar.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Long Hunt's chapter 2 places (docs/sendas/barbaro.md, «Las Vértebras»): where the titan's tail goes into the sea
 * and out of it arches of stone one after another, like the vertebrae of a beast going under. Wet grey stone over a
 * cold sea by day (foam bursting between the piers, spray on the wind), the last vertebra under a red dusk.
 *
 * - `vertSea` (the cinematic), `vertTip` (the opening): the tail's tip and the arches going out, lower and lower, Ivar's
 *   red-prowed boat by the shore below.
 * - `vertArches` (act 1): the way of arches, each head a vertebra on its pier (its wings and its spine of bone-stone),
 *   the spans between on their arches, a mooring post on each head (Sigrun's with five strokes and a knot). The gust's
 *   warning: the foam lifting in a line upwind; the gust: spray across the board.
 * - `vertBelow` (the choice and Sten's scene): one arch, Sigrun's old rope down to the sea; Ivar's boat caught between
 *   two rocks below, the swell, wolves swimming at it.
 * - `vertLeaps` (act 2): ten arches and the vertebra on their piers, Sigrun's ropes across the gaps; each arch's cracks
 *   (one, two) on its top, its fall (down into the sea, a column of foam), Sigrun's three marks (posts with five strokes
 *   and a knot, lit cold until touched, red after); the moment: the arches going into the sea behind, one after another.
 * - `vertFire` (the campfire), `vertDen` (act 3, the test), `vertClose`: the last vertebra, wide, its cave low to the
 *   north with real boars' bones at the mouth; the cracked slabs (their cracks, their sinking: a hole of dark water);
 *   the loose stones; Bera's long boat on the slip, going down into the water as she works; the red dusk. The close on
 *   the sea in Bera's boat, Ivar's far off.
 *
 * Terrón is drawn here (the herd's body and legs), on the field and in the scenes.
 *
 * Draw calls: the dressing (one merged mesh), each leaps arch (its own, for its fall), the ropes and the cracks
 * (instanced), the marks, the den's slabs, stones and boat, the sea, Terrón, the spray, the glow layer, the sky. No
 * lights of its own: the board's are repainted (HOURS).
 */

export const VERT_PLACES = new Set(['vertSea', 'vertTip', 'vertArches', 'vertBelow', 'vertLeaps', 'vertFire', 'vertDen', 'vertClose']);

/** The scenes' boards' seed (client game/cine.ts builds the same). */
export const VERT_SCENE_SEED = 7412;

const S = WORLD_PER_HEX;
const K = S / 1000;
/** A hex's corner radius, in world units. */
const HR = S / Math.sqrt(3);
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const COLD = new THREE.Color('#dfeaf6');
const FIRE = new THREE.Color('#ff9a5a');
const FIRE_CORE = new THREE.Color('#fff0e6');
const WET = ['#6c737b', '#646b73', '#737a82', '#5d646c'];
const DEEP = ['#4a5259', '#434b52', '#50585f'];
const WEED = ['#34453f', '#2e3d38', '#3b4b44'];
const BONE = '#b7bec4';
const WOOD = ['#4a3d32', '#3e342b', '#55473a'];
const ROPE = '#8b8274';
const CLAN = '#a43a2c';
const TERRON = new THREE.Color('#c9d6c4');

type Hour = 'day' | 'dusk';

/** The light: an overcast day over a cold sea, readable; the red dusk at the last vertebra. */
const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; sea: [string, string, string] }> = {
  day: { bg: '#5f6d7b', fog: ['#76838f', 34, 120], hemi: ['#d6dfe9', '#3a3f45', 1.95], key: ['#eef3fb', 1.85], rim: ['#a3b6cc', 0.8], sky: ['#3f5064', '#9aa9b8', '#eef2f8'], sea: ['#2e4a5a', '#5d7f90', '#e6eef4'] },
  // (The key warm but low, the sky's cold strong: the stone stays grey under the red sky, never pink.)
  // (The fog cold too: a warm one laid over the floor turned the whole vertebra mauve.)
  dusk: { bg: '#2b1d24', fog: ['#2c303a', 30, 104], hemi: ['#c2cee2', '#2a2f36', 2.4], key: ['#ff9a78', 0.95], rim: ['#8fa0e0', 1.1], sky: ['#1e1828', '#c4533c', '#ffb28c'], sea: ['#232a3a', '#4b4458', '#dfe2ea'] },
};

/** The sea: a slow swell, broken foam, a sheen; its colours by the hour. */
const seaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, deep: { value: new THREE.Color() }, shallow: { value: new THREE.Color() }, foamC: { value: new THREE.Color() } },
    fog: false,
    vertexShader: /* glsl */ `
      varying vec2 vXZ; varying float vD;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; vec4 v = viewMatrix * w; vD = -v.z; gl_Position = projectionMatrix * v; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform vec3 deep; uniform vec3 shallow; uniform vec3 foamC; varying vec2 vXZ; varying float vD;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float sw = n(vXZ * 0.18 + vec2(time * 0.07, time * 0.03)) * 0.6 + n(vXZ * 0.55 - vec2(time * 0.12, -time * 0.05)) * 0.4;
        // Foam in thin broken streaks along the swell, not in blobs.
        float fn = n(vec2(vXZ.x * 2.2 - time * 0.35, vXZ.y * 0.8 + sw * 3.0)) * n(vXZ * 1.7 + time * 0.1);
        float foam = smoothstep(0.42, 0.55, fn) * smoothstep(0.45, 0.7, sw);
        vec3 col = mix(deep, shallow, sw * 0.8);
        col = mix(col, foamC, foam * 0.55);
        // Far off it goes into the haze.
        col = mix(col, shallow * 1.1, smoothstep(30.0, 110.0, vD) * 0.6);
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/* ---------- the scenes' grounds ---------- */

interface Ways {
  /** The heads (sim points) and how wide each is (hexes, its floor's radius); the spans between consecutive ones. */
  heads: CazaPt[];
  wide: number[];
  spans: [number, number][];
  /** More floor (sim points with a radius in hexes): the tail's tip, the boat's footprint. */
  extra: { at: CazaPt; r: number }[];
}

const cellsOf = (R: number): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

const segD2 = (p: CazaPt, a: CazaPt, b: CazaPt): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
  const x = a.x + dx * t - p.x;
  const y = a.y + dy * t - p.y;
  return x * x + y * y;
};

const H = (q: number, r: number): CazaPt => hexToWorld(q, r);

/** The scenes' ways (the arches a scene shows), by place. */
const sceneWays = (place: string): Ways | null => {
  if (place === 'vertTip' || place === 'vertSea') {
    const heads = [H(0, 3), H(2, -2), H(-2, -6), H(3, -10), H(-1, -14), H(2, -18)];
    return { heads, wide: [3, 1, 1, 1, 1, 1], spans: heads.slice(1).map((_, i) => [i, i + 1] as [number, number]), extra: [{ at: H(0, 6), r: 3 }] };
  }
  if (place === 'vertBelow') {
    const heads = [H(-1, 5), H(0, 0), H(2, -5)];
    return { heads, wide: [1, 2, 1], spans: [[0, 1], [1, 2]], extra: [] };
  }
  if (place === 'vertFire') return { heads: [H(0, 0)], wide: [5], spans: [], extra: [] };
  if (place === 'vertClose') return { heads: [], wide: [], spans: [], extra: [] };
  return null;
};

const SCENE_R: Record<string, number> = { vertTip: 9, vertSea: 18, vertBelow: 7, vertFire: 7, vertClose: 6 };

/** A scene's board (client game/cine.ts): its floor the ways', the rest the sea. */
export const vertSceneMap = (place: string): MapDef | null => {
  const w = sceneWays(place);
  if (!w) return null;
  const R = SCENE_R[place] as number;
  const m = createClearingMap(R);
  const rocks: { q: number; r: number }[] = [];
  for (const h of cellsOf(m.radius)) {
    const p = hexToWorld(h.q, h.r);
    let ok = false;
    w.heads.forEach((c, i) => {
      const rr = ((w.wide[i] as number) * 1000 + 150) ** 2;
      if ((p.x - c.x) ** 2 + (p.y - c.y) ** 2 <= rr) ok = true;
    });
    for (const [a, b] of w.spans) if (!ok && segD2(p, w.heads[a] as CazaPt, w.heads[b] as CazaPt) <= 640 * 640) ok = true;
    for (const e of w.extra) if (!ok && (p.x - e.at.x) ** 2 + (p.y - e.at.y) ** 2 <= (e.r * 1000 + 150) ** 2) ok = true;
    if (!ok) rocks.push(h);
  }
  return { ...m, rocks };
};

/* ---------- the layer ---------- */

interface ArchMesh {
  g: THREE.Group;
  at: THREE.Vector3;
  fall: number;
  shown: boolean;
}

export class VertLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow: GlowLayer;
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private spray: Precip | null = null;
  private sea: THREE.ShaderMaterial | null = null;
  private seaY = -2.8;
  private now = 0;
  private hour: Hour;
  private rnd = looksRng(0x7e27);
  private floor = new Set<string>();
  private mat: THREE.MeshStandardMaterial;
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private embers: THREE.Vector3[] = [];
  private nextEmber = 0;
  private nextFoam = 0;
  private piers: THREE.Vector3[] = [];
  private look = new THREE.Vector3();
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private e3 = new THREE.Euler();
  private zero = new THREE.Matrix4().makeScale(0, 0, 0);
  private last: BattleState | null = null;
  // The gusts (act 1): the warning's side and until when; the gust's spray.
  private warnDir = 0;
  private warnUntil = 0;
  private gustAt = 0;
  private gustDir = 0;
  // The leaps (act 2).
  private arches: ArchMesh[] = [];
  private ropes: THREE.InstancedMesh | null = null;
  private ropeOf: { a: number; b: number; from: number; n: number }[] = [];
  private cracks: THREE.InstancedMesh | null = null;
  private crackOf: { arch: number; level: number; m: THREE.Matrix4 }[] = [];
  private crackKey = '';
  private marks: THREE.Group[] = [];
  private markAt: number[] = [];
  private markLit: boolean[] = [];
  private touchFlash = 0;
  private sinkAll = -1;
  // The den (act 3).
  private slabs: { g: THREE.Group; c1: THREE.Object3D; c2: THREE.Object3D; hole: THREE.Object3D; at: THREE.Vector3; sunk: number }[] = [];
  private stones: THREE.Mesh[] = [];
  private boat: THREE.Group | null = null;
  private boatFrom = new THREE.Vector3();
  private boatTo = new THREE.Vector3();
  private boatK = 0;
  private mouth: THREE.Vector3 | null = null;
  // Terrón.
  private tBody: THREE.Mesh | null = null;
  private tLegs: THREE.Mesh[] = [];
  private tTrack = { px: 0, py: 0, cx: 0, cy: 0, dist: 0, set: false };

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `vert-${place}`;
    this.hour = place === 'vertFire' || place === 'vertDen' || place === 'vertClose' ? 'dusk' : 'day';
    for (const h of cellsOf(map.radius)) this.floor.add(`${h.q},${h.r}`);
    for (const h of map.rocks ?? []) this.floor.delete(`${h.q},${h.r}`);
    this.glow = new GlowLayer(ctx.low ? 160 : 320);
    this.mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.05, flatShading: true }));
    const kit = new Kit();
    const seed = s.rules?.senda ? s.seed : VERT_SCENE_SEED;
    if (place === 'vertArches') this.buildArches(kit, seed);
    else if (place === 'vertLeaps') this.buildLeaps(kit, seed);
    else if (place === 'vertDen') this.buildDen(kit, seed, true);
    else this.buildScene(kit);
    this.buildTerron();
    const dress = kit.build(this.mat, 'vert-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // Spray on the wind off the sea.
    if (!ctx.crypt) {
      this.spray = new Precip({ count: ctx.low ? 160 : 320, box: [16, 6, 16], fall: 0.6, wind: [3.6, 0.8], sway: 0.6, size: [0.03, 0.08], color: this.hour === 'day' ? '#eef3f8' : '#f0dcdc', opacity: 0.28, round: true }, 91);
      this.group.add(this.spray.mesh);
    }
    this.buildSea();
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private light(p: THREE.Vector3, color: THREE.Color, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: color, size, a, seed: this.lights.length * 1.37, flick });
  }

  private at(p: CazaPt, h = 0): THREE.Vector3 {
    return new THREE.Vector3(p.x * K, h, p.y * K);
  }

  private isFloor(x: number, z: number): boolean {
    const h = worldToHex(Math.round(x / K), Math.round(z / K));
    return this.floor.has(`${h.q},${h.r}`);
  }

  private buildSea(): void {
    const sea = this.track(seaMat());
    const look = HOURS[this.hour];
    (sea.uniforms.deep as THREE.IUniform<THREE.Color>).value.set(look.sea[0]);
    (sea.uniforms.shallow as THREE.IUniform<THREE.Color>).value.set(look.sea[1]);
    (sea.uniforms.foamC as THREE.IUniform<THREE.Color>).value.set(look.sea[2]);
    this.sea = sea;
    const m = new THREE.Mesh(this.track(new THREE.PlaneGeometry(260, 260, 1, 1)), sea);
    m.rotation.x = -Math.PI / 2;
    m.position.y = this.seaY;
    m.name = 'vert-sea';
    m.renderOrder = -2;
    this.group.add(m);
  }

  /** A hex of the way's deck from under the board's tile down to `bottom` (the slab its top lies on). */
  private deck(kit: Kit, x: number, z: number, bottom: number, k: number): void {
    const geo = new THREE.CylinderGeometry(HR * 1.02, HR * 0.94, 1, 6, 1);
    const top = -0.02;
    kit.add(geo, WET[k % WET.length] as string, place(x, (top + bottom) / 2, z, 0, new THREE.Vector3(1, top - bottom, 1)));
    geo.dispose();
  }

  /**
   * A vertebra on its pier: a tapered column of wet stone down into the sea (weed and barnacles at the waterline), two
   * wings out to the sides under the deck, and a crest of bone-stone knobs along its outer edge.
   */
  private vertebra(kit: Kit, c: THREE.Vector3, r: number, yaw: number, seed: number, crest = true): void {
    const rnd = looksRng(seed);
    const col = new THREE.CylinderGeometry(1, 1, 1, 7, 1);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const y0 = -0.35;
    const y1 = this.seaY - 0.6;
    const steps = 5;
    for (let i = 0; i < steps; i++) {
      const a = y0 + ((y1 - y0) * i) / steps;
      const b = y0 + ((y1 - y0) * (i + 1)) / steps;
      const w = r * (1.0 - 0.1 * i) + rnd() * 0.08;
      const low = b < this.seaY + 0.9;
      const tone = low ? (WEED[i % WEED.length] as string) : i < 2 ? (WET[(seed + i) % WET.length] as string) : (DEEP[i % DEEP.length] as string);
      kit.add(col, tone, place(c.x + (rnd() - 0.5) * 0.12, (a + b) / 2, c.z + (rnd() - 0.5) * 0.12, rnd() * 3, new THREE.Vector3(w, a - b, w * 0.9)));
    }
    // The waterline: weed, and barnacles pale on it.
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2 + rnd();
      const rr = r * 0.62;
      kit.add(rock, i % 3 === 0 ? '#9aa3a9' : (WEED[i % 3] as string), place(c.x + Math.cos(a) * rr, this.seaY + 0.15 + rnd() * 0.3, c.z + Math.sin(a) * rr, a, 0.22 + rnd() * 0.18));
    }
    // The wings (transverse processes), out to either side under the deck.
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    for (const k of [-1, 1]) {
      const ox = cy * k * r * 1.05;
      const oz = -sy * k * r * 1.05;
      kit.add(rock, '#7d858c', place(c.x + ox * 1.1, -0.22, c.z + oz * 1.1, yaw, new THREE.Vector3(r * 1.1, 0.4, 0.62), 0, k * 0.28));
      kit.add(rock, '#8c949a', place(c.x + ox * 1.6, -0.42, c.z + oz * 1.6, yaw + 0.3, new THREE.Vector3(0.62, 0.34, 0.44), 0, k * 0.5));
    }
    // The crest: knobs of pale bone-stone along the outer side, rising a little over the deck.
    if (crest)
      for (let i = 0; i < 4; i++) {
        const t = (i - 1.5) * 0.45;
        const px = c.x + -sy * t * r + cy * r * 0.82;
        const pz = c.z + -cy * t * r - sy * r * 0.82;
        if (this.isFloor(px, pz) && i !== 0) continue;
        kit.add(rock, i % 2 ? '#8e959b' : '#a0a7ac', place(px, 0.05 + rnd() * 0.12, pz, rnd() * 3, new THREE.Vector3(0.3, 0.5 + rnd() * 0.3, 0.26)));
      }
    this.piers.push(new THREE.Vector3(c.x, this.seaY, c.z));
    col.dispose();
    rock.dispose();
  }

  /** The arch under a span: a ring of stone from one pier's foot up under the deck's middle and down to the next. */
  private spanArch(kit: Kit, a: THREE.Vector3, b: THREE.Vector3, seed: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const n = 12;
    const yaw = Math.atan2(b.x - a.x, b.z - a.z);
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const lo = this.seaY + 0.4;
    const hi = -0.55;
    let px = a.x;
    let pz = a.z;
    let py = lo;
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const x = a.x + (b.x - a.x) * t;
      const z = a.z + (b.z - a.z) * t;
      const y = lo + (hi - lo) * Math.pow(Math.sin(Math.PI * t), 0.55);
      const mx = (x + px) / 2;
      const my = (y + py) / 2;
      const mz = (z + pz) / 2;
      const seg = Math.hypot(x - px, y - py, z - pz);
      const pitch = Math.atan2(y - py, Math.hypot(x - px, z - pz));
      kit.add(box, (i + seed) % 4 === 0 ? (DEEP[i % 3] as string) : (WET[(i + seed) % 4] as string), place(mx, my, mz, yaw, new THREE.Vector3(0.95, 0.42, seg + 0.06), -pitch, 0));
      px = x;
      py = y;
      pz = z;
    }
    // The deck's underside along the span: a beam of stone under the walkway.
    kit.add(box, DEEP[seed % 3] as string, place((a.x + b.x) / 2, -0.4, (a.z + b.z) / 2, yaw, new THREE.Vector3(0.8, 0.3, len * 0.8)));
    box.dispose();
  }

  /** A mooring post: a black-tarred post, an iron ring, a coil of rope; Sigrun's with five strokes and a knot. */
  private post(kit: Kit, p: THREE.Vector3, sigrun: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const y = this.ctx.ground(p.x, p.z);
    kit.add(box, '#2f2a26', place(p.x, y + 0.5, p.z, 0.3, new THREE.Vector3(0.18, 1.0, 0.18)));
    kit.add(box, '#3a332c', place(p.x, y + 1.02, p.z, 0.3, new THREE.Vector3(0.22, 0.06, 0.22)));
    const tor = new THREE.TorusGeometry(0.13, 0.025, 6, 12);
    kit.add(tor, '#5a5e64', place(p.x + 0.1, y + 0.78, p.z, 0.3, 1, 0, Math.PI / 2));
    const coil = new THREE.TorusGeometry(0.15, 0.04, 6, 12);
    kit.add(coil, ROPE, place(p.x, y + 0.36, p.z, 0, 1, Math.PI / 2, 0));
    kit.add(coil, ROPE, place(p.x, y + 0.43, p.z, 0.6, 0.92, Math.PI / 2, 0));
    if (sigrun) this.sigrunMark(kit, p, y);
    tor.dispose();
    coil.dispose();
    box.dispose();
  }

  /** Sigrun's mark on a post: five pale strokes cut into its face, a knot and a strip of clan red under it. */
  private sigrunMark(kit: Kit, p: THREE.Vector3, y: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < 5; i++) kit.add(box, '#e6ebef', place(p.x - 0.06 + i * 0.03, y + 0.66, p.z + 0.095, 0.3, new THREE.Vector3(0.012, 0.16, 0.012), 0, i === 4 ? 1.1 : 0));
    kit.add(new THREE.SphereGeometry(0.06, 6, 5), ROPE, place(p.x, y + 0.52, p.z + 0.1, 0));
    kit.add(box, CLAN, place(p.x, y + 0.47, p.z + 0.1, 0.3, new THREE.Vector3(0.05, 0.12, 0.015), 0, 0.2));
    box.dispose();
  }

  /** A long boat of the clan: low and open, six oars, shields on the gunwale, a boar's head on the prow (Ivar's red). */
  private longboat(kit: Kit, at: THREE.Vector3, yaw: number, red: boolean, sc = 1): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    const P = (lx: number, ly: number, lz: number): [number, number, number] => [at.x + (lx * cy + lz * sy) * sc, at.y + ly * sc, at.z + (-lx * sy + lz * cy) * sc];
    const n = 9;
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1) - 0.5;
      const w = 0.62 * Math.cos(t * Math.PI * 0.92) + 0.04;
      const rise = Math.pow(Math.abs(t) * 2, 3) * 0.32;
      const [x, y, z] = P(0, -0.1 + rise, t * 4.2);
      // Keel and strakes: a floor and two sides that flare.
      kit.add(box, '#3b3029', place(x, y - 0.18, z, yaw, new THREE.Vector3(w * 1.3 * sc, 0.1 * sc, 0.5 * sc)));
      for (const k of [-1, 1]) {
        const [sx, sy2, sz] = P(k * w * 0.72, rise + 0.02, t * 4.2);
        kit.add(box, i % 2 ? '#4b3d33' : '#54453a', place(sx, sy2, sz, yaw, new THREE.Vector3(0.07 * sc, 0.42 * sc, 0.52 * sc), 0, -k * 0.32));
      }
    }
    // Benches, the oars out on each side, the shields along the gunwale.
    for (let i = 0; i < 3; i++) {
      const lz = -1.1 + i * 1.1;
      const [x, y, z] = P(0, 0.02, lz);
      kit.add(box, '#5c4b3d', place(x, y, z, yaw, new THREE.Vector3(1.0 * sc, 0.05 * sc, 0.16 * sc)));
      for (const k of [-1, 1]) {
        const [ox, oy, oz] = P(k * 1.15, -0.06, lz);
        kit.add(box, '#6a5846', place(ox, oy, oz, yaw + k * 0.15, new THREE.Vector3(1.7 * sc, 0.04 * sc, 0.05 * sc), 0, k * 0.22));
        const [bx, by, bz] = P(k * 1.95, -0.22, lz);
        kit.add(box, '#6a5846', place(bx, by, bz, yaw, new THREE.Vector3(0.05 * sc, 0.04 * sc, 0.2 * sc)));
      }
    }
    const shield = new THREE.CylinderGeometry(0.19, 0.19, 0.04, 10);
    for (let i = 0; i < 5; i++)
      for (const k of [-1, 1]) {
        const [x, y, z] = P(k * 0.66, 0.28, -1.5 + i * 0.75);
        kit.add(shield, (i + (k > 0 ? 1 : 0)) % 2 ? CLAN : '#5b6168', place(x, y, z, yaw + Math.PI / 2, sc, Math.PI / 2, 0));
      }
    // The prow's boar head (red for Ivar), and the stern post.
    const [hx, hy, hz] = P(0, 0.55, 2.25);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    kit.add(rock, red ? '#a83526' : '#5d544c', place(hx, hy, hz, yaw, new THREE.Vector3(0.32 * sc, 0.34 * sc, 0.5 * sc)));
    const [tx, ty, tz] = P(0, 0.62, 2.48);
    kit.add(new THREE.ConeGeometry(0.06, 0.3, 5), '#d0d4d8', place(tx, ty, tz, yaw, sc, 1.2, 0));
    const [px, py, pz] = P(0, 0.5, -2.2);
    kit.add(box, '#3b3029', place(px, py, pz, yaw, new THREE.Vector3(0.1 * sc, 0.7 * sc, 0.12 * sc), -0.4, 0));
    if (red) {
      const [rx, ry, rz] = P(0, 0.18, 1.85);
      kit.add(box, '#b23a2a', place(rx, ry, rz, yaw, new THREE.Vector3(1.0 * sc, 0.3 * sc, 0.7 * sc)));
    }
    shield.dispose();
    rock.dispose();
    box.dispose();
  }

  /** A campfire on the stone: a ring of stones, logs, its flames' glow and embers. */
  private fire(kit: Kit, p: THREE.Vector3): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      kit.add(rock, WET[i % 4] as string, place(p.x + Math.cos(a) * 0.42, p.y + 0.06, p.z + Math.sin(a) * 0.42, a, 0.2));
    }
    for (let i = 0; i < 4; i++) kit.add(box, WOOD[i % 3] as string, place(p.x, p.y + 0.06, p.z, (i / 4) * Math.PI + 0.3, new THREE.Vector3(0.08, 0.08, 0.55), 0.25, 0));
    kit.add(new THREE.CylinderGeometry(0.16, 0.2, 0.06, 8), '#ff7a3a', place(p.x, p.y + 0.05, p.z));
    box.dispose();
    rock.dispose();
    const f = new THREE.Vector3(p.x, p.y + 0.3, p.z);
    this.light(f, FIRE_CORE, 0.9, 0.95, 1);
    this.light(f.clone().setY(f.y + 0.2), FIRE, 2.2, 0.75, 1);
    this.light(f.clone().setY(p.y + 0.1), FIRE, 4.2, 0.22, 0.5);
    this.embers.push(f);
  }

  /** A rock standing out of the sea. */
  private seaRock(kit: Kit, x: number, z: number, s: number, seed: number): void {
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const rnd = looksRng(seed);
    for (let i = 0; i < 4; i++) kit.add(rock, i < 2 ? (DEEP[i] as string) : (WEED[i % 3] as string), place(x + (rnd() - 0.5) * s * 0.6, this.seaY + s * (0.5 - i * 0.12), z + (rnd() - 0.5) * s * 0.6, rnd() * 3, new THREE.Vector3(s * (1 - i * 0.12), s * 1.3, s * 0.9)));
    this.piers.push(new THREE.Vector3(x, this.seaY, z));
    rock.dispose();
  }

  /** The deck under every floor cell, and the piers and arches of a set of ways. */
  private buildWays(kit: Kit, heads: CazaPt[], wide: number[], spans: [number, number][], crest = true): void {
    let k = 0;
    for (const key of this.floor) {
      const [q, r] = key.split(',').map(Number) as [number, number];
      const p = hexToWorld(q, r);
      this.deck(kit, p.x * K, p.y * K, -0.5, k++);
    }
    heads.forEach((c, i) => {
      const n = heads[Math.min(heads.length - 1, i + 1)] as CazaPt;
      const b = heads[Math.max(0, i - 1)] as CazaPt;
      const yaw = Math.atan2(n.x - b.x, n.y - b.y) + Math.PI / 2;
      this.vertebra(kit, this.at(c), ((wide[i] ?? 1) + 0.55) * S * 0.75, yaw, 31 + i * 7, crest);
    });
    for (const [a, b] of spans) this.spanArch(kit, this.at(heads[a] as CazaPt), this.at(heads[b] as CazaPt), a * 3 + b);
  }

  /* ---------- act 1: the arches in the wind ---------- */

  private buildArches(kit: Kit, seed: number): void {
    const L = archesLayout(seed);
    this.buildWays(
      kit,
      L.heads,
      L.heads.map((_, i) => (i === 0 || i === L.heads.length - 1 ? 1.8 : 1.1)),
      L.heads.slice(1).map((_, i) => [i, i + 1] as [number, number]),
    );
    L.posts.forEach((p, i) => this.post(kit, this.at(p), i === L.mark));
    // Rocks out in the sea either side, the waves bursting on them.
    const rnd = looksRng(seed ^ 0x55);
    for (let i = 0; i < 14; i++) {
      const z = (rnd() * 2 - 1) * 16 * S;
      const x = (rnd() < 0.5 ? -1 : 1) * (7 + rnd() * 8) * S;
      if (!this.isFloor(x, z)) this.seaRock(kit, x, z, 0.6 + rnd() * 0.9, i + 3);
    }
    // The last vertebra far ahead (north), low and wide with its cave, and the tail behind (south).
    this.seaRock(kit, 2 * S, -26 * S, 3.2, 77);
    this.seaRock(kit, -1 * S, 22 * S, 3.6, 78);
  }

  /* ---------- act 2: the leaps ---------- */

  private buildLeaps(kit: Kit, seed: number): void {
    const L = leapsLayout(seed);
    const n = L.arches.length;
    // The deck under each arch goes with it (its own mesh, for its fall).
    L.arches.forEach((c, k) => {
      const ak = new Kit();
      const R = k === n - 1 ? LEAP_VERT : LEAP_ARCH;
      const h = worldToHex(c.x, c.y);
      let j = 0;
      for (const d of cellsOf(R)) {
        const p = hexToWorld(h.q + d.q, h.r + d.r);
        this.deck(ak, p.x * K, p.y * K, -0.5, j++ + k);
      }
      this.vertebra(ak, this.at(c), (R + 0.55) * S * 0.8, k * 1.1, 51 + k * 5);
      if (k === n - 1) {
        // The last vertebra: its cave low on the far side, the boat beyond.
        const rock = new THREE.DodecahedronGeometry(0.5, 0);
        for (let i = 0; i < 6; i++) ak.add(rock, DEEP[i % 3] as string, place(c.x * K + (i - 2.5) * 0.7, 0.4 + (i % 2) * 0.3, c.y * K - 2.4 * S, i, new THREE.Vector3(1.1, 1.2, 0.9)));
        ak.add(rock, '#15171b', place(c.x * K + 0.3, 0.3, c.y * K - 2.0 * S, 0, new THREE.Vector3(1.0, 0.7, 0.3)));
        rock.dispose();
      }
      const mesh = ak.build(this.mat, `vert-arch-${k}`);
      const g = new THREE.Group();
      if (mesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        this.geos.push(mesh.geometry);
        g.add(mesh);
      }
      this.group.add(g);
      this.arches.push({ g, at: this.at(c), fall: -1, shown: true });
    });
    // Sigrun's ropes across the gaps: a sagging line of segments each (instanced, one mesh).
    const seg = 10;
    this.ropeOf = L.graph.edges.map(([a, b], i) => ({ a, b, from: i * seg, n: seg }));
    const ropes = new THREE.InstancedMesh(this.track(new THREE.CylinderGeometry(0.022, 0.022, 1, 5)), this.track(new THREE.MeshStandardMaterial({ color: ROPE, roughness: 0.95 })), this.ropeOf.length * seg);
    for (const r of this.ropeOf) {
      const A = this.at(L.arches[r.a] as CazaPt);
      const B = this.at(L.arches[r.b] as CazaPt);
      const ra = (r.a === n - 1 ? LEAP_VERT : LEAP_ARCH) * S * 0.85;
      const rb = (r.b === n - 1 ? LEAP_VERT : LEAP_ARCH) * S * 0.85;
      const d = Math.hypot(B.x - A.x, B.z - A.z);
      const ux = (B.x - A.x) / d;
      const uz = (B.z - A.z) / d;
      const a0 = new THREE.Vector3(A.x + ux * ra, 0.55, A.z + uz * ra);
      const b0 = new THREE.Vector3(B.x - ux * rb, 0.55, B.z - uz * rb);
      for (let i = 0; i < seg; i++) {
        const t0 = i / seg;
        const t1 = (i + 1) / seg;
        const p0 = a0.clone().lerp(b0, t0).setY(0.55 - Math.sin(Math.PI * t0) * 0.75);
        const p1 = a0.clone().lerp(b0, t1).setY(0.55 - Math.sin(Math.PI * t1) * 0.75);
        const mid = p0.clone().add(p1).multiplyScalar(0.5);
        const dir = p1.clone().sub(p0);
        this.q4.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
        this.m4.compose(mid, this.q4, this.s3.set(1, dir.length(), 1));
        ropes.setMatrixAt(r.from + i, this.m4);
      }
      // A post at each end, the rope tied to it.
      for (const [p, sgn] of [
        [a0, 1],
        [b0, -1],
      ] as [THREE.Vector3, number][]) {
        const box = new THREE.BoxGeometry(1, 1, 1);
        kit.add(box, '#2f2a26', place(p.x - ux * 0.1 * sgn, 0.3, p.z - uz * 0.1 * sgn, Math.atan2(ux, uz), new THREE.Vector3(0.12, 0.7, 0.12)));
        box.dispose();
      }
    }
    ropes.instanceMatrix.needsUpdate = true;
    ropes.frustumCulled = false;
    ropes.castShadow = true;
    this.ropes = ropes;
    this.group.add(ropes);
    // The cracks on each arch's top: one, then a second across it (instanced, shown as they come).
    const box = this.track(new THREE.BoxGeometry(1, 1, 1));
    const crackMat = this.track(new THREE.MeshBasicMaterial({ color: '#0e1114' }));
    const rnd = looksRng(seed ^ 0x3c);
    L.arches.forEach((c, k) => {
      if (k === n - 1) return;
      const cx = c.x * K;
      const cz = c.y * K;
      for (const level of [1, 2]) {
        const a0 = (level === 1 ? 0.3 : 1.9) + rnd() * 0.6;
        let x = cx - Math.cos(a0) * 0.85;
        let z = cz - Math.sin(a0) * 0.85;
        for (let i = 0; i < 6; i++) {
          const a = a0 + (rnd() - 0.5) * 1.1;
          const len = 0.3;
          const nx = x + Math.cos(a) * len;
          const nz = z + Math.sin(a) * len;
          const m = new THREE.Matrix4().compose(new THREE.Vector3((x + nx) / 2, 0.0, (z + nz) / 2), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -a, 0)), new THREE.Vector3(len + 0.04, 0.03, 0.05 + level * 0.015));
          this.crackOf.push({ arch: k, level, m });
          x = nx;
          z = nz;
        }
      }
    });
    const cracks = new THREE.InstancedMesh(box, crackMat, this.crackOf.length);
    for (let i = 0; i < this.crackOf.length; i++) cracks.setMatrixAt(i, this.zero);
    cracks.frustumCulled = false;
    cracks.renderOrder = 3;
    this.cracks = cracks;
    this.group.add(cracks);
    // Sigrun's three marks: a post each with its strokes and knot, moved if their arch falls.
    for (let i = 0; i < 3; i++) {
      const mk = new Kit();
      this.post(mk, new THREE.Vector3(0, 0, 0), true);
      const mesh = mk.build(this.mat, `vert-mark-${i}`);
      const g = new THREE.Group();
      if (mesh) {
        this.geos.push(mesh.geometry);
        g.add(mesh);
      }
      this.group.add(g);
      this.marks.push(g);
      this.markAt.push(-1);
      this.markLit.push(false);
    }
    this.placeMarks(L.graph.marks, [false, false, false], L.arches);
  }

  private placeMarks(marks: number[], touched: boolean[], arches: CazaPt[]): void {
    marks.forEach((m, i) => {
      const g = this.marks[i];
      if (!g) return;
      if (m < 0) {
        g.visible = false;
        return;
      }
      if (this.markAt[i] !== m) {
        const c = arches[m] as CazaPt;
        g.position.set(c.x * K + 0.5, this.ctx.ground(c.x * K, c.y * K) - 0.02, c.y * K - 0.35);
        g.visible = true;
        this.markAt[i] = m;
      }
      this.markLit[i] = !!touched[i];
    });
  }

  /* ---------- act 3: the den (and its scenes) ---------- */

  private buildDen(kit: Kit, seed: number, field: boolean): void {
    const L = denLayout(seed);
    let k = 0;
    for (const key of this.floor) {
      const [q, r] = key.split(',').map(Number) as [number, number];
      const p = hexToWorld(q, r);
      this.deck(kit, p.x * K, p.y * K, -0.6, k++);
    }
    // The vertebra itself: one great pier under the floor, its wings east and west.
    this.vertebra(kit, new THREE.Vector3(0, 0, 0), 8.2 * S * 0.72, 0, 99, false);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const rnd = looksRng(seed ^ 0x99);
    // The cave's mound to the north: piled dark rock, the mouth a low black arch facing the floor.
    for (const h of L.cave) {
      const p = hexToWorld(h.q, h.r);
      const d = hexLen(h.q - 3, h.r + 6);
      const tall = 1.2 + rnd() * 0.9 + Math.max(0, 3 - d) * 0.15;
      kit.add(rock, DEEP[(h.q + h.r + 9) % 3] as string, place(p.x * K + (rnd() - 0.5) * 0.3, tall * 0.4, p.y * K, rnd() * 3, new THREE.Vector3(1.5, tall, 1.3)));
      if (rnd() < 0.35) kit.add(rock, WEED[h.q % 3 < 0 ? 0 : h.q % 3] as string, place(p.x * K, tall * 0.85, p.y * K, rnd() * 3, new THREE.Vector3(0.7, 0.3, 0.6)));
    }
    const m = this.at(L.mouth);
    this.mouth = m.clone();
    kit.add(rock, '#0c0d10', place(m.x, 0.35, m.z - 0.95, 0, new THREE.Vector3(2.4, 1.3, 0.7)));
    kit.add(rock, '#3a3f45', place(m.x - 1.3, 0.8, m.z - 0.55, 0.4, new THREE.Vector3(0.9, 1.7, 0.9)));
    kit.add(rock, '#3a3f45', place(m.x + 1.3, 0.8, m.z - 0.55, -0.4, new THREE.Vector3(0.9, 1.7, 0.9)));
    kit.add(rock, '#444a50', place(m.x, 1.55, m.z - 0.6, 0, new THREE.Vector3(3.0, 0.6, 1.0)));
    this.light(new THREE.Vector3(m.x, 0.5, m.z - 0.5), RED, 1.4, 0.16, 0.3);
    // Boars' bones at the mouth: ribs, a skull or two, long bones (grey under the red sky, never cream).
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < 16; i++) {
      const a = rnd() * Math.PI * 2;
      const rr = 0.6 + rnd() * 2.2;
      const x = m.x + Math.cos(a) * rr;
      const z = m.z + 0.6 + Math.abs(Math.sin(a)) * rr * 0.6;
      if (i % 5 === 0) {
        kit.add(rock, BONE, place(x, 0.1, z, a, new THREE.Vector3(0.28, 0.2, 0.36)));
        kit.add(new THREE.ConeGeometry(0.03, 0.18, 5), '#d4d9dd', place(x + 0.12, 0.12, z + 0.12, a, 1, 0, 1.4));
      } else kit.add(box, i % 2 ? BONE : '#a9b0b6', place(x, 0.04, z, a, new THREE.Vector3(0.05, 0.05, 0.4 + rnd() * 0.3), 0, 0.3));
    }
    // The floor strewn: bones of the pack's meals, broken rock, tufts of salt grass in the seams (cold, never mauve),
    // kept off the slabs, the stones, the slip and the cave's mouth so nothing that matters is hidden.
    const keepOff = new Set<string>();
    for (const sl of L.slabs) for (const h of sl.cells) keepOff.add(`${h.q},${h.r}`);
    const near = (x: number, z: number, p: CazaPt, r: number): boolean => (x - p.x * K) ** 2 + (z - p.y * K) ** 2 < r * r;
    const tuft = new THREE.ConeGeometry(0.05, 0.28, 3);
    for (let i = 0; i < 150; i++) {
      const a = rnd() * Math.PI * 2;
      const rr = Math.sqrt(rnd()) * 7.6 * S;
      const x = Math.cos(a) * rr;
      const z = Math.sin(a) * rr;
      if (!this.isFloor(x, z)) continue;
      const hx = worldToHex(Math.round(x / K), Math.round(z / K));
      if (keepOff.has(`${hx.q},${hx.r}`)) continue;
      if (near(x, z, L.boat, 2.2 * S) || near(x, z, L.mouth, 1.6 * S) || L.stones.some((p) => near(x, z, p, 1.1 * S))) continue;
      const y = this.ctx.ground(x, z);
      const kind = i % 6;
      if (kind === 0 || kind === 1) {
        // A long bone, or two crossed.
        kit.add(box, kind ? BONE : '#a3abb2', place(x, y + 0.03, z, a, new THREE.Vector3(0.05, 0.05, 0.3 + rnd() * 0.3)));
        if (kind) kit.add(box, '#9aa2a9', place(x + 0.05, y + 0.04, z, a + 1.2, new THREE.Vector3(0.045, 0.045, 0.24)));
      } else if (kind === 2) {
        // A rib cage half sunk, ribs curving up.
        for (let j = 0; j < 4; j++) kit.add(box, BONE, place(x + Math.cos(a) * j * 0.09, y + 0.08, z + Math.sin(a) * j * 0.09, a + Math.PI / 2, new THREE.Vector3(0.035, 0.2, 0.035), 0, 0.5));
      } else if (kind === 3) {
        // Broken rock, a cluster.
        for (let j = 0; j < 3; j++) kit.add(rock, (WET[(i + j) % 4] as string), place(x + (rnd() - 0.5) * 0.5, y + 0.06, z + (rnd() - 0.5) * 0.5, rnd() * 3, 0.18 + rnd() * 0.22));
      } else {
        // A tuft of salt grass, grey-green.
        for (let j = 0; j < 4; j++) kit.add(tuft, ['#4f5d52', '#46544a', '#5a6a5c'][j % 3] as string, place(x + (rnd() - 0.5) * 0.18, y + 0.12, z + (rnd() - 0.5) * 0.18, rnd() * 3, new THREE.Vector3(1, 0.7 + rnd() * 0.6, 1), (rnd() - 0.5) * 0.4, (rnd() - 0.5) * 0.4));
      }
    }
    tuft.dispose();
    // Big boulders on the rim, so the edge reads as an edge.
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2 + rnd() * 0.2;
      const x = Math.cos(a) * 7.7 * S;
      const z = Math.sin(a) * 7.7 * S;
      if (!this.isFloor(x, z) || z > 4 * S || near(x, z, L.mouth, 2.5 * S) || L.stones.some((p) => near(x, z, p, 1.6 * S))) continue;
      kit.add(rock, DEEP[i % 3] as string, place(x, this.ctx.ground(x, z) + 0.25, z, rnd() * 3, new THREE.Vector3(0.9 + rnd() * 0.5, 0.6 + rnd() * 0.5, 0.8 + rnd() * 0.4)));
    }
    // Spray rocks out in the sea round it.
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 + rnd() * 0.4;
      this.seaRock(kit, Math.cos(a) * 12.5 * S, Math.sin(a) * 12.5 * S, 0.7 + rnd() * 1.1, 200 + i);
    }
    // The slip at the south and Bera's boat on it.
    const boat = new Kit();
    this.longboat(boat, new THREE.Vector3(0, 0, 0), 0, false);
    const bm = boat.build(this.mat, 'vert-boat');
    const g = new THREE.Group();
    if (bm) {
      bm.castShadow = true;
      this.geos.push(bm.geometry);
      g.add(bm);
    }
    const bp = this.at(L.boat);
    this.boatFrom.set(bp.x, 0.18, bp.z - 0.6);
    this.boatTo.set(bp.x, this.seaY + 0.42, bp.z + 3.4);
    g.position.copy(this.boatFrom);
    g.rotation.set(0.18, 0, 0);
    this.boat = g;
    this.group.add(g);
    // Rollers on the slip.
    for (let i = 0; i < 4; i++) kit.add(new THREE.CylinderGeometry(0.08, 0.08, 1.2, 6), WOOD[i % 3] as string, place(bp.x, -0.02, bp.z - 1.4 + i * 0.8, 0, 1, 0, Math.PI / 2));
    if (!field) {
      box.dispose();
      rock.dispose();
      return;
    }
    // The slabs: plates of paler stone with their seams, two levels of crack and the hole they leave.
    L.slabs.forEach((sl, i) => {
      const pk = new Kit();
      const c1 = new Kit();
      const c2 = new Kit();
      const at = this.at(sl.at);
      for (const h of sl.cells) {
        const p = hexToWorld(h.q, h.r);
        const geo = new THREE.CylinderGeometry(HR * 0.9, HR * 0.92, 0.08, 6);
        pk.add(geo, (h.q + h.r) % 2 ? '#7a8692' : '#6e7a87', place(p.x * K - at.x, 0.02, p.y * K - at.z));
        geo.dispose();
      }
      const crack = (kk: Kit, a0: number, w: number): void => {
        let x = -Math.cos(a0) * 0.9;
        let z = -Math.sin(a0) * 0.9;
        for (let j = 0; j < 7; j++) {
          const a = a0 + Math.sin(j * 2.1 + i) * 0.6;
          const nx = x + Math.cos(a) * 0.28;
          const nz = z + Math.sin(a) * 0.28;
          kk.add(box, '#101215', place((x + nx) / 2, 0.075, (z + nz) / 2, -a, new THREE.Vector3(0.32, 0.03, w)));
          x = nx;
          z = nz;
        }
      };
      crack(c1, 0.4 + i, 0.06);
      crack(c2, 2.1 + i, 0.08);
      const mk = (kk: Kit, name: string): THREE.Object3D => {
        const mesh = kk.build(this.mat, name);
        if (mesh) this.geos.push(mesh.geometry);
        return mesh ?? new THREE.Object3D();
      };
      const sg = new THREE.Group();
      sg.position.copy(at);
      const plate = mk(pk, `vert-slab-${i}`);
      (plate as THREE.Mesh).receiveShadow = true;
      const k1 = mk(c1, `vert-slab-c1-${i}`);
      const k2 = mk(c2, `vert-slab-c2-${i}`);
      k1.visible = false;
      k2.visible = false;
      sg.add(plate, k1, k2);
      this.group.add(sg);
      // The hole: dark water where it was, a ring of foam.
      const hole = new THREE.Mesh(this.track(new THREE.CircleGeometry(1.25, 18)), this.track(new THREE.MeshBasicMaterial({ color: '#0d1820', transparent: true, opacity: 0.92 })));
      hole.rotation.x = -Math.PI / 2;
      hole.position.set(at.x, 0.06, at.z);
      hole.visible = false;
      this.group.add(hole);
      this.slabs.push({ g: sg, c1: k1, c2: k2, hole, at, sunk: -1 });
    });
    // The loose stones on the rim.
    const sgeo = this.track(new THREE.DodecahedronGeometry(0.55, 0));
    const smat = this.track(new THREE.MeshStandardMaterial({ color: '#7b8187', roughness: 0.9, flatShading: true }));
    for (let i = 0; i < L.stones.length; i++) {
      const sm = new THREE.Mesh(sgeo, smat);
      sm.castShadow = true;
      const p = this.at(L.stones[i] as CazaPt);
      sm.position.set(p.x, 0.5, p.z);
      sm.scale.set(1.1, 0.9, 1);
      this.stones.push(sm);
      this.group.add(sm);
    }
    box.dispose();
    rock.dispose();
  }

  /* ---------- the scenes ---------- */

  private buildScene(kit: Kit): void {
    const w = sceneWays(this.place);
    if (this.place === 'vertClose') {
      // On the sea: Bera's boat under the four of them, Ivar's far off in the Wolf's wake.
      this.seaY = -0.62;
      this.longboat(kit, new THREE.Vector3(0, -0.15, 0), Math.PI / 6 + Math.PI / 2, false, 1.05);
      this.longboat(kit, new THREE.Vector3(-4.2 * S, this.seaY + 0.35, -10 * S), 0.2, true, 0.8);
      for (let i = 0; i < 6; i++) this.seaRock(kit, (i - 2.5) * 5 * S, -16 * S - (i % 2) * 3 * S, 1.2 + (i % 3) * 0.5, 400 + i);
      return;
    }
    if (this.place === 'vertFire') {
      // The last vertebra under its last arch: the den's ground, the arch over the fire, the boat on its slip.
      this.buildDen(kit, VERT_SCENE_SEED, false);
      const a = new THREE.Vector3(-4.2 * S, 0, 0.5 * S);
      const b = new THREE.Vector3(4.2 * S, 0, -0.5 * S);
      const box = new THREE.BoxGeometry(1, 1, 1);
      const n = 16;
      let p0 = a.clone();
      for (let i = 1; i <= n; i++) {
        const t = i / n;
        const p = a.clone().lerp(b, t).setY(Math.sin(Math.PI * t) * 4.6);
        const mid = p0.clone().add(p).multiplyScalar(0.5);
        const len = p.distanceTo(p0);
        const pitch = Math.atan2(p.y - p0.y, Math.hypot(p.x - p0.x, p.z - p0.z));
        kit.add(box, WET[i % 4] as string, place(mid.x, mid.y, mid.z, Math.atan2(b.x - a.x, b.z - a.z), new THREE.Vector3(1.3, 0.9, len + 0.1), -pitch, 0));
        p0 = p;
      }
      box.dispose();
      this.fire(kit, new THREE.Vector3(0, this.ctx.ground(0, 0), 0));
      return;
    }
    if (!w) return;
    this.buildWays(kit, w.heads, w.wide, w.spans);
    if (this.place === 'vertTip' || this.place === 'vertSea') {
      // The tail's tip: grass on the stone, a cairn; the arches going out lower and lower; Ivar's boat below by the shore.
      const cone = new THREE.ConeGeometry(0.06, 0.32, 3);
      const rnd = looksRng(0x71);
      for (let i = 0; i < 160; i++) {
        const x = (rnd() * 2 - 1) * 4 * S;
        const z = (3 + rnd() * 5) * S;
        if (!this.isFloor(x, z)) continue;
        kit.add(cone, ['#55654b', '#4c5b44', '#5e6e53'][i % 3] as string, place(x, this.ctx.ground(x, z) + 0.13, z, i, new THREE.Vector3(1, 0.8 + rnd() * 0.6, 1)));
      }
      cone.dispose();
      w.heads.slice(1).forEach((c, i) => this.post(kit, this.at({ x: c.x + 900, y: c.y }), i === 2));
      this.longboat(kit, new THREE.Vector3(5 * S, this.seaY + 0.35, 1 * S), 0.5, true, 0.95);
      for (let i = 0; i < 8; i++) this.seaRock(kit, (i % 2 ? -1 : 1) * (6 + i) * S, (-4 - i * 3) * S, 0.8 + (i % 3) * 0.4, 300 + i);
      return;
    }
    if (this.place === 'vertBelow') {
      // Sigrun's old rope from a post down the arch's side to the sea; Ivar's boat caught between two rocks below,
      // wolves swimming at it.
      const post = this.at(H(1, -1));
      this.post(kit, post, true);
      const box = new THREE.BoxGeometry(1, 1, 1);
      const top = new THREE.Vector3(post.x + 0.6, 0.0, post.z);
      for (let i = 0; i < 9; i++) {
        const t = i / 9;
        const p = new THREE.Vector3(top.x + t * 1.6, -t * (2.6 + 0.2), top.z - t * 0.8);
        kit.add(box, ROPE, place(p.x, p.y, p.z, 0.4, new THREE.Vector3(0.035, 0.34, 0.035), 0, -0.5));
      }
      box.dispose();
      const bt = new THREE.Vector3(4.2 * S, this.seaY + 0.35, -2.2 * S);
      this.longboat(kit, bt, 1.1, true, 0.95);
      this.seaRock(kit, bt.x - 1.8, bt.z + 2.4, 1.4, 501);
      this.seaRock(kit, bt.x + 1.6, bt.z - 2.6, 1.6, 502);
      const head = new THREE.SphereGeometry(0.16, 7, 5);
      for (let i = 0; i < 5; i++) kit.add(head, '#3a3d45', place(bt.x - 3 + i * 0.9, this.seaY + 0.05, bt.z + 3 + (i % 2) * 0.8, 0, new THREE.Vector3(1, 0.7, 1.6)));
      head.dispose();
    }
  }

  /* ---------- Terrón ---------- */

  private buildTerron(): void {
    const mat = this.track(new THREE.MeshStandardMaterial({ color: TERRON, roughness: 0.88, flatShading: true }));
    this.tBody = new THREE.Mesh(this.track(boarBody(13)), mat);
    this.tBody.castShadow = true;
    this.tBody.visible = false;
    this.group.add(this.tBody);
    const leg = this.track(boarLeg());
    for (let i = 0; i < 4; i++) {
      const m = new THREE.Mesh(leg, mat);
      m.visible = false;
      this.tLegs.push(m);
      this.group.add(m);
    }
  }

  private drawTerron(s: BattleState | null, alpha: number): void {
    const body = this.tBody;
    if (!body) return;
    const tn = s?.senda?.npcs.find((x) => x.id === 'terron' && x.hp > 0);
    let x: number;
    let y: number;
    let facing: number;
    let moving = false;
    let lie = false;
    if (tn) {
      const t = this.tTrack;
      x = t.set ? t.px + (t.cx - t.px) * alpha : tn.x;
      y = t.set ? t.py + (t.cy - t.py) * alpha : tn.y;
      facing = tn.facing;
      moving = tn.moving || (t.set && (t.cx !== t.px || t.cy !== t.py));
    } else {
      const fig = s?.heroes.find((h) => h.face === 'terron');
      const p = fig ? this.ctx.pos(fig.id) : undefined;
      if (!fig || !p) {
        body.visible = false;
        for (const l of this.tLegs) l.visible = false;
        return;
      }
      x = p.x / K;
      y = p.z / K;
      facing = fig.facing ?? 0;
      lie = this.place === 'vertFire' || this.place === 'vertClose';
    }
    const sc = SCALE.hero * 1.2;
    const g = this.ctx.at(x, y, lie ? -0.08 : 0);
    const stride = moving ? this.tTrack.dist * 9.5 : 0;
    const bob = moving ? Math.abs(Math.sin(stride)) * 0.025 : Math.max(0, Math.sin(this.now * 0.9)) * 0.01;
    const yaw = facingToRotY(facing);
    this.e3.set(0, yaw, Math.sin(stride) * 0.03, 'YXZ');
    body.position.set(g.x, g.y + bob, g.z);
    body.quaternion.setFromEuler(this.e3);
    body.scale.setScalar(sc);
    body.visible = true;
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    this.tLegs.forEach((l, i) => {
      const [lx, lz] = BOAR_LEGS[i] as [number, number];
      const ph = i === 0 || i === 3 ? 1 : -1;
      this.e3.set(lie ? 1.2 * ph : Math.sin(stride) * 0.5 * ph, yaw, 0, 'YXZ');
      l.quaternion.setFromEuler(this.e3);
      l.position.set(g.x + (lx * cy + lz * sy) * sc, g.y + BOAR_HIP_Y * sc + bob, g.z + (-lx * sy + lz * cy) * sc);
      l.scale.setScalar(sc);
      l.visible = true;
    });
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const c2 = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const tip = this.place === 'vertTip' || this.place === 'vertSea';
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        // Wet slate in two tones, darker in the hollows; on the tail's tip, grass over it.
        const k = 0.5 + 0.25 * Math.sin(v.x * 0.41 + v.z * 0.23) * Math.cos(v.z * 0.33 - v.x * 0.19) + 0.25 * Math.sin(v.x * 1.1 + 1.3) * Math.sin(v.z * 0.9 + 0.4);
        c.set(this.hour === 'dusk' ? '#66727e' : '#6a7279').lerp(c2.set(this.hour === 'dusk' ? '#434c57' : '#565e66'), Math.max(0, Math.min(1, k)));
        // At dusk, a speckle of paler and darker stone so the floor isn't one flat tone.
        if (this.hour === 'dusk') {
          const sp = Math.sin(v.x * 3.7 + v.z * 1.9) * Math.sin(v.z * 4.3 - v.x * 2.3);
          if (sp > 0.55) c.lerp(c2.set('#7d868d'), 0.45);
          else if (sp < -0.6) c.lerp(c2.set('#3b4149'), 0.5);
        }
        if (tip && v.z > 3 * S) c.lerp(c2.set('#4a5a43'), 0.7);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads' || o.name === 'outskirts-ground') o.visible = false;
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    this.carve(terrain);
    if (terrain.fog) terrain.fog.group.visible = true;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint();
  }

  /** The sea everywhere but the floor: the board's ground cut away there. */
  private carve(terrain: TerrainBuild): void {
    const mesh = terrain.mesh;
    const geo = mesh.geometry;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    mesh.updateMatrixWorld(true);
    const mw = mesh.matrixWorld;
    const v = new THREE.Vector3();
    const idx = geo.index;
    const tri = (i: number): number => (idx ? idx.getX(i) : i);
    const n = idx ? idx.count : pos.count;
    const keep: number[] = [];
    for (let i = 0; i < n; i += 3) {
      let cx = 0;
      let cz = 0;
      for (let k = 0; k < 3; k++) {
        v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
        cx += v.x;
        cz += v.z;
      }
      const out = !this.isFloor(cx / 3, cz / 3);
      if (idx) {
        if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
      } else if (out) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        pos.setXYZ(i + 1, x, y, z);
        pos.setXYZ(i + 2, x, y, z);
      }
    }
    if (idx) geo.setIndex(keep);
    pos.needsUpdate = true;
    geo.computeBoundingSphere();
  }

  paint(): void {
    const look = HOURS[this.hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(this.hour === 'dusk' ? 0.2 : -0.4, this.hour === 'dusk' ? 0.08 : 0.35, -0.9);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    this.last = s;
    const t = s.senda?.npcs.find((n) => n.id === 'terron');
    if (t) {
      const k = this.tTrack;
      if (!k.set) Object.assign(k, { px: t.x, py: t.y, cx: t.x, cy: t.y, dist: 0, set: true });
      else {
        k.dist += Math.hypot(t.x - k.cx, t.y - k.cy) * K;
        k.px = k.cx;
        k.py = k.cy;
        k.cx = t.x;
        k.cy = t.y;
      }
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.1);
      switch (e.kind) {
        case 'vGustWarn':
          this.warnDir = e.n ?? 1;
          this.warnUntil = this.now + 3;
          break;
        case 'vGust':
          this.gustAt = this.now;
          this.gustDir = e.n ?? 1;
          this.ctx.shake(0.12);
          break;
        case 'vOver':
        case 'vFall':
          this.splash(new THREE.Vector3(p.x, this.seaY, p.z), 1);
          break;
        case 'vNudge':
          this.ctx.vfx.burst(p, { n: 10, color: '#9aa3a9', speed: 0.03, up: 0.08, size: 0.1, life: 20, gravity: 0.006 });
          break;
        case 'vArrive': {
          const n = e.n ?? 0;
          const c = n % 10;
          this.ctx.vfx.burst(p, { n: 6 + c * 4, color: '#8d949a', speed: 0.035, up: 0.05, size: 0.09, life: 18, gravity: 0.006 });
          if (c === 2) this.ctx.shake(0.05);
          break;
        }
        case 'vArchFall': {
          const a = this.arches[e.n ?? -1];
          if (a && a.fall < 0) {
            a.fall = this.now;
            this.ctx.shake(0.25);
          }
          break;
        }
        case 'vTouch':
          this.touchFlash = this.now;
          this.ctx.vfx.ring(p.clone().setY(0.1), 0.3, 2.6, '#ff8a6e', 16, 0.9);
          if ((e.n ?? 0) >= 3) this.ctx.vfx.ring(p.clone().setY(0.1), 0.6, 6, '#eef3f8', 24, 0.7);
          break;
        case 'vMarkMoved':
          this.ctx.vfx.ring(p.clone().setY(0.1), 2.2, 0.3, '#eef3f8', 18, 0.8);
          break;
        case 'vLeapsDone':
          this.sinkAll = this.now + 0.6;
          this.ctx.vfx.ring(p.clone().setY(0.1), 0.5, 4.5, '#ff8a6e', 20, 0.9);
          break;
        case 'vRope':
          if ((e.n ?? -1) < 0) this.ctx.vfx.ring(p.clone().setY(0.1), 0.6, 0.2, '#ff4f3a', 8, 0.7);
          break;
        case 'vSlabCrack':
          this.ctx.vfx.burst(p, { n: 8, color: '#8a9096', speed: 0.03, up: 0.05, size: 0.08, life: 16, gravity: 0.006 });
          break;
        case 'vSlabCreak':
          this.ctx.vfx.ring(p.clone().setY(0.12), 1.4, 0.4, '#ff4f3a', 14, 0.9);
          this.ctx.shake(0.06);
          break;
        case 'vSlabSink': {
          const sl = this.slabs[Math.floor((e.n ?? 0) / 100)];
          if (sl && sl.sunk < 0) sl.sunk = this.now;
          this.splash(new THREE.Vector3(p.x, 0, p.z), 0.8);
          this.ctx.shake(0.18);
          break;
        }
        case 'vLaunched':
          this.splash(this.boatTo.clone(), 0.9);
          break;
        case 'vRush':
          if (this.mouth) this.ctx.vfx.burst(this.mouth.clone().setY(0.4), { n: 30, color: '#2a2c33', speed: 0.06, up: 0.05, size: 0.22, life: 30, smoke: true, spread: 0.8 });
          break;
        case 'stonePush':
          this.ctx.vfx.burst(p, { n: 10, color: '#7b8187', speed: 0.04, up: 0.06, size: 0.1, life: 16, gravity: 0.006 });
          break;
        case 'stoneHit':
          this.ctx.vfx.burst(p, { n: 14, color: '#c9ced3', speed: 0.06, up: 0.08, size: 0.12, life: 18, gravity: 0.008 });
          this.ctx.shake(0.08);
          break;
        case 'cutHowl':
          this.ctx.vfx.ring(p.clone().setY(0.3), 0.3, 2.0, '#ff8a6e', 12, 0.9);
          break;
        case 'howled':
        case 'vDouble':
          this.ctx.vfx.ring(p.clone().setY(0.4), 0.5, e.kind === 'vDouble' ? 9 : 5, '#e8eef8', 22, 0.7);
          break;
        case 'rise':
          this.ctx.vfx.burst(p, { n: 12, color: '#3a3d45', speed: 0.03, up: 0.07, size: 0.18, life: 24, smoke: true });
          break;
      }
    }
  }

  /** A column of foam out of the sea (an arch gone, a wolf over the edge, a slab sunk). */
  private splash(p: THREE.Vector3, big: number): void {
    this.ctx.vfx.burst(p.clone().setY(this.seaY + 0.1), { n: Math.round(26 * big), color: '#eef3f8', speed: 0.05 * big, up: 0.16 * big, size: 0.2 * big, life: 34, gravity: 0.008, spread: 0.6 });
    this.ctx.vfx.ring(p.clone().setY(this.seaY + 0.05), 0.4, 3 * big, '#e6eef6', 20, 0.8);
  }

  draw(s: BattleState): void {
    this.last = s;
    const lr = s.rules?.senda?.vert?.leaps;
    const lw = s.senda?.vert?.leaps;
    if (lr && lw) {
      // The cracks as they come, the ropes of fallen arches gone, the marks where they are.
      const key = `${lw.cracks.join('')}|${lw.fallen.map((f) => (f ? 1 : 0)).join('')}`;
      if (key !== this.crackKey && this.cracks && this.ropes) {
        this.crackKey = key;
        this.crackOf.forEach((c, i) => this.cracks!.setMatrixAt(i, !lw.fallen[c.arch] && (lw.cracks[c.arch] as number) >= c.level ? c.m : this.zero));
        this.cracks.instanceMatrix.needsUpdate = true;
        for (const r of this.ropeOf)
          if (lw.fallen[r.a] || lw.fallen[r.b]) {
            for (let i = 0; i < r.n; i++) this.ropes.setMatrixAt(r.from + i, this.zero);
          }
        this.ropes.instanceMatrix.needsUpdate = true;
      }
      this.placeMarks(lw.marks, lw.touched, lr.arches);
    }
    const dr = s.rules?.senda?.vert?.den;
    const dw = s.senda?.vert?.den;
    if (dr && dw) {
      dw.slabs.forEach((sl, i) => {
        const m = this.slabs[i];
        if (!m) return;
        m.c1.visible = sl.cracks >= 1;
        m.c2.visible = sl.cracks >= 2;
      });
      dw.stones.forEach((st, i) => {
        const m = this.stones[i];
        if (!m) return;
        if (st.mode === 2) {
          m.visible = false;
          return;
        }
        m.position.set(st.x * K, 0.5, st.y * K);
        if (st.mode === 1) m.rotation.z -= 0.35;
      });
      this.boatK = Math.min(1, dw.work / Math.max(1, dr.launch));
    }
  }

  frame(now: number, camera?: THREE.Camera, alpha = 1): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (camera) lookPoint(camera, this.look);
    this.spray?.frame(now, this.look);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    this.drawTerron(this.last, alpha);
    // The waves bursting on the piers, now and then. (The vfx's particles are walked in script every frame: a burst of
    // few, big ones a little over a second apart near the camera reads the same as many often, at a fraction of the
    // cost; docs/RENDIMIENTO.md, «Las Vértebras».)
    if (this.piers.length && now > this.nextFoam) {
      this.nextFoam = now + (this.ctx.low ? 1.6 : 0.9);
      const p = this.piers[Math.floor(this.rnd() * this.piers.length)] as THREE.Vector3;
      if (p.distanceToSquared(this.look) < 18 * 18) this.ctx.vfx.burst(p.clone().setY(this.seaY + 0.2), { n: 5, color: '#eef3f8', speed: 0.035, up: 0.08, size: 0.24, life: 24, gravity: 0.006, spread: 1.0 });
    }
    // The gust's warning: the foam lifting in a line upwind of where the hero is; the gust, spray across.
    if (now < this.warnUntil) {
      const x0 = this.look.x - this.warnDir * 6.5;
      if (Math.floor(now * 5) !== Math.floor((now - dt) * 5))
        for (let i = 0; i < 4; i++) {
          const z = this.look.z + (i - 2) * 2.6 + this.rnd();
          this.ctx.vfx.burst(new THREE.Vector3(x0, this.seaY + 0.2, z), { n: 3, color: '#f4f8fb', speed: 0.02, up: 0.09 + (1 - (this.warnUntil - now) / 3) * 0.08, size: 0.22, life: 30, gravity: 0.002, spread: 0.3 });
        }
    }
    if (this.gustAt && now - this.gustAt < 0.8 && Math.floor(now * 10) !== Math.floor((now - dt) * 10)) {
      for (let i = 0; i < 3; i++) {
        const z = this.look.z + (this.rnd() - 0.5) * 10;
        const x = this.look.x - this.gustDir * (4 - (now - this.gustAt) * 10) + (this.rnd() - 0.5);
        this.ctx.vfx.burst(new THREE.Vector3(x, 0.4 + this.rnd() * 0.6, z), { n: 2, color: '#eef3f8', speed: 0.08, up: 0.01, size: 0.12, life: 14, gravity: 0, spread: 0.2 });
      }
    }
    // The leaps: arches going down into the sea; the moment, every arch behind going one after another.
    if (this.sinkAll > 0 && now > this.sinkAll) {
      let k = 0;
      this.arches.forEach((a, i) => {
        if (i === this.arches.length - 1 || a.fall >= 0) return;
        a.fall = now + k++ * 0.35;
      });
      this.sinkAll = -1;
    }
    for (const a of this.arches) {
      if (a.fall < 0 || now < a.fall || !a.shown) continue;
      const t = now - a.fall;
      if (t < 0.05 && Math.floor(now * 60) !== Math.floor((now - dt) * 60)) this.splash(a.at.clone(), 1.3);
      a.g.position.y = -t * t * 3.2;
      a.g.rotation.z = Math.sin(a.at.x) * t * 0.35;
      a.g.rotation.x = Math.cos(a.at.z) * t * 0.25;
      if (t > 1.8) {
        a.g.visible = false;
        a.shown = false;
      }
    }
    // The den: the slabs going down, the boat sliding down the slip as Bera works it.
    for (const sl of this.slabs) {
      if (sl.sunk < 0) continue;
      const t = now - sl.sunk;
      sl.g.position.y = -t * t * 2.4;
      if (t > 1.2) sl.g.visible = false;
      sl.hole.visible = true;
    }
    if (this.boat) {
      const k = this.boatK;
      // Most of the work is the push; the last part it goes down into the water.
      const e = k < 0.85 ? k * 0.25 : 0.21 + ((k - 0.85) / 0.15) * 0.79;
      this.boat.position.lerpVectors(this.boatFrom, this.boatTo, e);
      this.boat.rotation.x = 0.18 * (1 - e);
      if (e >= 1) this.boat.position.y = this.boatTo.y + Math.sin(now * 1.4) * 0.05;
    }
    // Embers off the fire.
    if (this.embers.length && now > this.nextEmber) {
      this.nextEmber = now + (this.ctx.low ? 0.35 : 0.18);
      const e = this.embers[Math.floor(this.rnd() * this.embers.length)] as THREE.Vector3;
      this.ctx.vfx.burst(e, { n: 1, color: '#ff9a5a', speed: 0.006, up: 0.03, size: 0.05, life: 30, gravity: -0.0008, spread: 0.2 });
    }
    this.glow.begin();
    for (const l of this.lights) {
      const fl = l.flick ? 0.84 + 0.16 * Math.sin(now * (6 + l.seed) + l.seed) * l.flick + 0.06 * Math.sin(now * 17 + l.seed * 3) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * fl);
    }
    // Sigrun's marks: cold light until touched, red after; the third touched, a breath of light over the board.
    this.marks.forEach((g, i) => {
      if (!g.visible) return;
      const pulse = 0.5 + 0.5 * Math.sin(now * 2.4 + i);
      const lit = this.markLit[i];
      this.glow.add(g.position.x, g.position.y + 0.75, g.position.z + 0.1, lit ? RED : COLD, lit ? 0.9 : 1.1 + pulse * 0.4, lit ? 0.55 : 0.4 + pulse * 0.3);
    });
    if (this.touchFlash && now - this.touchFlash < 1.2) this.glow.add(this.look.x, this.look.y + 6, this.look.z, RED_SOFT, 26, (1.2 - (now - this.touchFlash)) * 0.05);
    // The den's howlers: rearing, a red ring that breathes; howling, pale rings going out.
    const d = this.last?.senda?.vert?.den;
    if (d)
      for (const hw of d.howlers) {
        if (hw.phase !== 1 && hw.phase !== 2) continue;
        const e = this.last?.enemies.find((x) => x.id === hw.id);
        const p = e ? (this.ctx.pos(e.id) ?? this.ctx.at(e.x, e.y, 0)) : null;
        if (!p) continue;
        const pulse = 0.5 + 0.5 * Math.sin(now * (hw.phase === 1 ? 10 : 20));
        this.glow.add(p.x, p.y + 0.9, p.z, RED, 2.2 + pulse, 0.45 + 0.4 * pulse);
        if (hw.phase === 2 && Math.floor(now * 4) !== Math.floor((now - dt) * 4)) this.ctx.vfx.ring(p.clone().setY(p.y + 0.5), 0.4, 3.4, '#e8eef8', 10, 0.55);
        if (hw.phase === 1 && Math.floor(now * 3) !== Math.floor((now - dt) * 3)) this.ctx.vfx.ring(p.clone().setY(p.y + 0.06), 1.8, 0.5, '#ff4f3a', 10, 0.8);
      }
    // The slabs creaking: a red breath under them.
    if (d)
      d.slabs.forEach((sl, i) => {
        const m = this.slabs[i];
        if (!m || sl.sunk || sl.creak < 0) return;
        this.glow.add(m.at.x, 0.2, m.at.z, RED, 2.6, 0.35 + 0.3 * Math.sin(now * 18));
      });
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.spray?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
